/**
 * Logo candidates found on an organization's website, each with a
 * "Use this" button. Used on the Organization Logos page and in the logo
 * editor. See docs/frontend/components/organization-logo.md.
 */
import { HiCheck } from 'react-icons/hi';
import type { LogoCandidate } from '../../types/api';

// Checkerboard behind previews, so transparent and white logos are visible
const CHECKERBOARD: React.CSSProperties = {
  backgroundImage: 'repeating-conic-gradient(#d1d5db 0 25%, #ffffff 0 50%)',
  backgroundSize: '16px 16px',
};

interface LogoCandidateGridProps {
  candidates: LogoCandidate[];
  onUse: (candidate: LogoCandidate) => void;
  /** URL being saved right now */
  savingUrl?: string | null;
  /** URL saved as the logo during this visit */
  savedUrl?: string | null;
  disabled?: boolean;
}

export const LogoCandidateGrid: React.FC<LogoCandidateGridProps> = ({
  candidates,
  onUse,
  savingUrl,
  savedUrl,
  disabled,
}) => (
  <ul className="flex flex-wrap gap-3">
    {candidates.map((candidate, index) => {
      const saved = candidate.url === savedUrl;
      const saving = candidate.url === savingUrl;
      return (
        <li
          key={candidate.url}
          className={`flex w-36 flex-col items-center gap-1 rounded-lg border-2 p-2 ${
            saved ? 'border-green-500' : 'border-gray-200 dark:border-gray-600'
          }`}
        >
          <div className="flex h-28 w-28 items-center justify-center overflow-hidden rounded" style={CHECKERBOARD}>
            <img src={candidate.thumbnail} alt={`Option ${index + 1}: ${candidate.label}`} className="max-h-28 max-w-28 object-contain" />
          </div>
          <div className="text-center text-xs text-gray-600 dark:text-gray-300">
            {candidate.label}
            {index === 0 && <span className="ml-1 font-medium text-blue-600 dark:text-blue-400">· Best guess</span>}
            <div className="text-gray-400">{candidate.width}×{candidate.height}</div>
          </div>
          {saved ? (
            <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-green-700 dark:text-green-400">
              <HiCheck className="h-4 w-4" /> Current logo
            </span>
          ) : (
            <button
              type="button"
              onClick={() => onUse(candidate)}
              disabled={disabled || !!savingUrl}
              className="rounded bg-blue-600 px-3 py-1 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Use this'}
            </button>
          )}
        </li>
      );
    })}
  </ul>
);
